import React, { useEffect, useState } from 'react';
import { Button, Input, Spin, Modal, Table } from 'antd';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../constants';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { selectAllCompanies } from '../../modules/company/stores/slice';
import { doGetCompanies, doRemoveCompany } from '../../modules/company/stores/thunk';

const CompaniesPage = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchParams] = useSearchParams();
  let location = useLocation();
  let navigate = useNavigate();
  const isFetching = useSelector((state) => state.company.isFetching);
  const metadata = useSelector((state) => state.company.metadata);
  const companies = useSelector((state) => selectAllCompanies(state));

  const query = Object.fromEntries(searchParams);
  const currentPage = query.current || 1;
  const itemsPerPage = query.pageSize || PAGE_SIZE;
  const pagination = {
    current: Number(currentPage) || 1,
    pageSize: Number(itemsPerPage),
    locale: { items_per_page: 'Companies/Page' },
    total: metadata.totalItems || companies.length,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showSizeChanger: true,
    showTotal: (total) => `${total} Companies`,
  };

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  useEffect(() => {
    dispatch(doGetCompanies({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.showSizeChanger;
    delete params.total;
    delete params.locale;
    delete params.page;
    if (updatePath) {
      navigate({
        pathname: location.pathname,
        search: queryString.stringify(params || {}, { skipNull: true }),
      });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(doGetCompanies({ page: newCurrentPage, take: newItemsPerPage, ...params }));
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const handleDelete = (record) => {
    setDeleteTarget(record);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(doRemoveCompany(deleteTarget.id)).unwrap();
      setDeleteTarget(null);
      dispatch(doGetCompanies({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (e) {
      // error handled globally
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => <Link to={`/companies/${record.id}`}>{text}</Link>,
    },
    {
      title: 'Owner',
      dataIndex: ['ownerClient', 'name'],
      key: 'owner',
    },
    {
      title: 'Clients',
      dataIndex: ['_count', 'clients'],
      key: 'clients',
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <div className="actions">
          <Button type="link" className="edit" onClick={() => navigate(`/companies/${record.id}`)}>
            <ReactSVG className="icon" src="/icons/edit.svg" />
          </Button>
          <div className="line"></div>
          <Button type="link" className="more" onClick={() => handleDelete(record)}>
            <ReactSVG className="icon" src="/icons/more.svg" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Spin spinning={false}>
      <div className="Content CompaniesPage">
        <div className="ContentHeader">
          <div className="HeaderTitle">COMPANIES</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons">
              <Link to="/companies/new">
                <Button className="CreateButton ButtonPrimary" type="primary" size="small">
                  Create New Company
                  <ReactSVG className="icon" src="/icons/add.svg" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <Modal
          title="Confirm Delete Company"
          open={!!deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          footer={[
            <Button key="cancel" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>,
            <Button key="delete" type="primary" danger loading={isFetching} onClick={handleConfirmDelete}>
              Delete
            </Button>,
          ]}
        >
          <p>
            Are you sure you want to delete company &ldquo;{deleteTarget?.name}&rdquo;? This action cannot be undone.
          </p>
        </Modal>

        <div className="orders-header-table page-header-table">
          <div className="search">
            <Input
              placeholder="Search"
              suffix={<img src="/icons/search.svg" />}
              onChange={(e) => setSearchValue(e.target.value)}
              onBlur={handleSearch}
              onKeyDown={handleKeyDown}
              disabled={isFetching}
              value={searchValue}
            />
          </div>
        </div>

        <div className="ContentMain">
          <Table
            columns={columns}
            dataSource={companies}
            pagination={pagination}
            rowKey="id"
            loading={isFetching}
            onChange={handleTableChange}
          />
        </div>
      </div>
    </Spin>
  );
};

export default CompaniesPage;
